import React from 'react';
import { CheckCircle2 } from 'lucide-react';

/**
 * Small green pill that surfaces when a Converge auto-capture has
 * landed for an order. Closes the customer's psychological loop:
 * "I approved → I got charged → my order is in motion."
 *
 * Renders nothing if the order hasn't been captured yet (or for
 * legacy orders that predate the payment_status field).
 *
 * Hover tooltip shows the precise capture timestamp so customers
 * trying to reconcile against their card statement can match it.
 *
 * Usage:
 *   <PaymentCapturedBadge
 *     paymentStatus={order.payment_status}
 *     capturedAt={order.payment_captured_at}
 *   />
 */
const formatCapturedAt = (raw) => {
  if (!raw) return '';
  try {
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return '';
    // Short, locale-aware "Apr 28, 2026 · 11:42 PM" style — matches the
    // way customers read their card statement.
    return d.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch (_) {
    return '';
  }
};

export const PaymentCapturedBadge = ({ paymentStatus, capturedAt, compact = false, testId = 'payment-captured-badge' }) => {
  if (paymentStatus !== 'captured') return null;
  const when = formatCapturedAt(capturedAt);
  const tooltip = when ? `Charged on ${when}` : 'Payment captured';
  return (
    <span
      data-testid={testId}
      title={tooltip}
      aria-label={tooltip}
      className={`inline-flex items-center gap-1 rounded-full border border-emerald-300 bg-emerald-50 text-emerald-800 font-semibold cursor-help ${
        compact ? 'px-1.5 py-0.5 text-[10px]' : 'px-2.5 py-1 text-xs'
      }`}
    >
      <CheckCircle2 className={compact ? 'w-3 h-3' : 'w-3.5 h-3.5'} />
      Payment captured
    </span>
  );
};

export default PaymentCapturedBadge;
